/**
 * useCandidateReports — the stored reports of ONE candidacy: the index read,
 * its polling while a kind builds, the Refresh from Thrive mutation, the
 * per-click downloads (attachment and inline) and the panel's preview URL.
 *
 * ONE CACHE ENTRY PER CANDIDACY, shared by the row's buttons and the panel.
 * A refresh pressed in the panel writes its 202 answer into that entry, so the
 * row behind the scrim turns to "Preparing…" in the same render.
 *
 * THE POLL BUDGET IS TIME, PER BUILDING EPISODE (CONTRACT-03-04 §4 "Leaving
 * `building`"; /pr-review #1021 iters 1–3). An episode of a kind starts when
 * the kind is first SEEN `building` — or when Refresh is pressed — and ends
 * when it leaves `building`. It has three phases, measured from its start:
 *
 *   - FAST, the first `CANDIDATE_REPORTS_MAX_POLLS` × `refresh_after_seconds`
 *     (20 × 3 s ≈ 60 s): polled at the server's interval;
 *   - SLOW, from there to `REFRESH_IN_FLIGHT_MS` (600 s): polled every
 *     `SLOW_POLL_FACTOR` intervals (15 s), and the panel says "Still preparing
 *     — this can take a few minutes" with NO Refresh prompt — the API ignores
 *     a refresh of a row in flight for less than 600 s, so offering one would
 *     be a button that does nothing;
 *   - STALLED, past 600 s: polling stops, the row shows a static
 *     "Preparing…", the panel "Still preparing — try Refresh from Thrive", and
 *     a Refresh — which the API now honours — starts a new episode.
 *
 * The clock counts VISIBLE time only (see `visibleNow`), and an episode is
 * tied to the stored version it builds on (see `episodes`). The client's start
 * (first sighting) is never earlier than the API's `building_since`, so its
 * 600 s never ends before the API's.
 *
 * Why time and not a request count: a count over the cache entry's lifetime
 * charged later episodes (and mount refetches) for earlier ones. (Two
 * observers do not double the rate for long: every answer re-arms each
 * observer's interval together and the coinciding fetch is de-duplicated, so
 * the most a second observer adds is one early tick when it mounts.) The
 * episodes live in a module map, written only where an answer arrives (the
 * query function and the refresh), so every observer reads the same start and
 * render only reads.
 *
 * Signed URLs are NEVER kept for a click: the API presigns for five minutes,
 * and a link fetched earlier would be dead by the time a slow reader clicks.
 * Downloads and "Open PDF" both ask per click. The preview iframe's URL is the
 * one kept, and only for the document already loaded in it.
 *
 * LIVE DATA ON CLICK (#874). A Download or "Open PDF" click asks with
 * `liveCheck: true`: the API reads Thrive first and answers 409
 * (`CandidateReportRebuildingError`) when the stored report is stale; a
 * rebuild is then queued. The click does not fail: its 409 body is written
 * into the shared entry exactly as a Refresh's 202 is (`seedBuildingAnswer`),
 * the button says "Updating from Thrive…", the entry polls as for any build,
 * and when the kind leaves `building` as `present` the click asks again with
 * `liveCheck: false` — the build just read Thrive — and opens the file ONCE.
 * A rebuild that GAVE UP also ends `present` (the worker puts the old files
 * back): its newer `last_error_at` tells it apart, and the click says so in an
 * error toast before opening the stored copy. A click soon after such a
 * failure is not sent through it again: the API serves the stored copy at
 * once with `rebuild_failed_at`, and the click shows the same toast (/pr-review
 * #1114 iter 2). A build past its stall budget (`isStalled`) ends the wait
 * with an error toast offering the stored copy. A 409 whose body does not
 * show the kind `building` (a race the worker had already finished) is written
 * into the shared entry and skips the wait: the stored file opens, or — when
 * the body shows the report gone — `REPORT_GONE` says so. The panel
 * preview never checks Thrive — it shows what is stored and reloads when a
 * new build lands (its key carries `built_at`). The wait reads the shared
 * entry and does not poll by itself: its callers (the Reports cell and the
 * panel) observe `useCandidateReports` for the same candidacy, which polls.
 */

import {
  type QueryClient,
  type UseQueryResult,
  hashKey,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";

import {
  type CandidateReportDownload,
  type CandidateReportFormat,
  type CandidateReportKind,
  CandidateReportNotFoundError,
  CandidateReportRebuildingError,
  type CandidateReportState,
  type CandidateReportsClient,
  type CandidateReportsResponse,
} from "../../../clients/candidateReports.client";

/** The FAST phase: this many intervals of `refresh_after_seconds`, measured
 *  in time — CONTRACT-03-04 §4. */
export const CANDIDATE_REPORTS_MAX_POLLS = 20;

/** The SLOW phase polls every this many intervals (15 s at the API's 3 s). */
export const SLOW_POLL_FACTOR = 5;

/**
 * How long a build must be in flight before the API honours a Refresh of it —
 * mirrors `REFRESH_IN_FLIGHT_SECONDS` (600) in
 * `packages/api/app/web/v2/candidate_reports/` (#1020). Change both together:
 * below it the client would offer a Refresh the API ignores.
 */
export const REFRESH_IN_FLIGHT_SECONDS = 600;
export const REFRESH_IN_FLIGHT_MS = REFRESH_IN_FLIGHT_SECONDS * 1000;

export const DOWNLOAD_FAILED = "The report could not be downloaded. Try again.";
export const OPEN_PDF_FAILED = "The PDF could not be opened. Try again.";
export const POPUP_BLOCKED = "Allow pop-ups to open the PDF.";
export const REFRESH_FAILED = "The report could not be refreshed from Thrive. Try again.";
/** The busy label while a click waits for its report's rebuild (#874). */
export const UPDATING_FROM_THRIVE = "Updating from Thrive…";
export const STILL_UPDATING =
  "The report is still updating from Thrive. You can open the stored copy instead.";
export const REPORT_GONE = "This report is no longer in Thrive.";
/** What a pre-opened "Open PDF" tab says while its report rebuilds (#874). */
export const UPDATING_TAB_MESSAGE = "Updating this report from Thrive…";
/** What it says before that, while the live check reads Thrive (up to 8 s). */
export const CHECKING_TAB_MESSAGE = "Checking this report against Thrive…";

/** `built_at` as a reader says it: "Oct 30, 2026, 5:05 PM". */
function formatReportTime(iso: string | null | undefined): string | null {
  const parsed = iso ? new Date(iso) : null;
  if (!parsed || Number.isNaN(parsed.getTime())) return null;
  return parsed.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

/** The error toast of a rebuild that gave up (#874): the click still serves
 *  the stored copy, and says which one. */
export function rebuildFailedMessage(
  builtAt: string | null | undefined,
  action: "Downloading" | "Opening"
): string {
  const when = formatReportTime(builtAt);
  const copy = when ? `the copy from ${when}` : "the stored copy";
  return `Couldn't update this report from Thrive. ${action} ${copy}.`;
}

/** An answered index is fresh this long: a remount or the panel opening on a
 *  row does not ask again. A building answer polls regardless; a refresh
 *  writes the cache directly. */
const REPORTS_STALE_MS = 30_000;

export function candidateReportsQueryKey(searchId: string, candidacyId: string) {
  return ["searches", "candidate-reports", searchId, candidacyId] as const;
}

/**
 * The episode clock: wall time MINUS the time this document spent hidden.
 * TanStack skips interval polls while the tab is hidden
 * (`refetchIntervalInBackground` is off), so a budget measured in wall time
 * would be spent by a reader who pressed Refresh and looked at another tab for
 * a minute — none of the 20 intervals used (/pr-review #1021 iter 2).
 */
function hiddenAtLoad(): number | null {
  // A tab opened in the background (a middle-clicked deep link) starts hidden:
  // its time until the first `visibilitychange` must not count either.
  return typeof document !== "undefined" && document.visibilityState === "hidden"
    ? Date.now()
    : null;
}

let hiddenSince: number | null = hiddenAtLoad();
let hiddenTotal = 0;

/** This module instance's cache subscriptions, released on HMR dispose. */
const cacheSubscriptions: Array<() => void> = [];

function onVisibilityChange(): void {
  if (document.visibilityState === "hidden") {
    if (hiddenSince === null) hiddenSince = Date.now();
  } else if (hiddenSince !== null) {
    hiddenTotal += Date.now() - hiddenSince;
    hiddenSince = null;
  }
}

if (typeof document !== "undefined") {
  document.addEventListener("visibilitychange", onVisibilityChange);
  // Under Vite HMR this module is re-evaluated: drop the old instance's
  // listener and cache subscriptions rather than stacking them.
  import.meta.hot?.dispose(() => {
    document.removeEventListener("visibilitychange", onVisibilityChange);
    for (const unsubscribe of cacheSubscriptions) unsubscribe();
  });
}

/** Now, on the episode clock (visible time only). */
export function visibleNow(): number {
  const now = Date.now();
  return now - hiddenTotal - (hiddenSince === null ? 0 : now - hiddenSince);
}

/**
 * Each building episode's start (on the episode clock) and the stored
 * `version_count` it was building on, by `search:candidacy:kind`.
 *
 * The version is what tells a NEW build from the old one when this tab missed
 * the end of the old: a stalled kind is not polled, so it never sees its build
 * land, and a later build of the same kind would otherwise inherit the spent
 * start and be born stalled (/pr-review #1021 iter 2). A build that lands with
 * NEW content bumps `version_count`, so a `building` answer on a different
 * version is a new episode; so is one whose cached predecessor was not
 * `building`. The entries of a candidacy are dropped when its query leaves the
 * cache.
 *
 * KNOWN LIMIT: a build that lands UNCHANGED (same fingerprint — the worker
 * writes `present` without a new version) does not bump `version_count`. If
 * this tab stalled, missed that landing, and kept the cached `building`
 * answer, the next build of the kind keeps the old start and is born stalled;
 * the panel then offers a Refresh. Closing it needs the index's
 * `building_since` (CONTRACT §4) in the API's answer — PRD 04 follow-up.
 */
const episodes = new Map<string, { start: number; version: number }>();

function episodeKey(searchId: string, candidacyId: string, kind: CandidateReportKind): string {
  return `${searchId}:${candidacyId}:${kind}`;
}

/**
 * Record what an answer says about episodes. A kind no longer `building` ends
 * its episode. A `building` kind starts one when it has none, when its
 * recorded version differs, or when `previous` (the cached answer this one
 * replaces) did not show it `building`.
 */
export function recordBuildingEpisodes(
  searchId: string,
  candidacyId: string,
  data: CandidateReportsResponse,
  now: number,
  previous?: CandidateReportsResponse
): void {
  for (const report of data.reports) {
    const key = episodeKey(searchId, candidacyId, report.kind);
    if (report.status !== "building") {
      episodes.delete(key);
      continue;
    }
    const recorded = episodes.get(key);
    const before = previous?.reports.find((r) => r.kind === report.kind);
    const isNew =
      recorded === undefined ||
      recorded.version !== report.version_count ||
      (before !== undefined && before.status !== "building");
    if (isNew) episodes.set(key, { start: now, version: report.version_count });
  }
}

/** Start a new episode for a kind (Refresh from Thrive). */
export function startBuildingEpisode(
  searchId: string,
  candidacyId: string,
  kind: CandidateReportKind,
  version: number,
  now: number
): void {
  episodes.set(episodeKey(searchId, candidacyId, kind), { start: now, version });
}

/** Drop a candidacy's episodes — its query left the cache. */
export function forgetBuildingEpisodes(searchId: string, candidacyId: string): void {
  const prefix = `${searchId}:${candidacyId}:`;
  for (const key of episodes.keys()) if (key.startsWith(prefix)) episodes.delete(key);
}

/** Forget every episode and the hidden time — for tests, which share the module. */
export function resetBuildingEpisodes(): void {
  episodes.clear();
  hiddenSince = hiddenAtLoad();
  hiddenTotal = 0;
}

/** The budget of one episode, in milliseconds, for an interval. */
export function episodeBudgetMs(refreshAfterSeconds: number): number {
  return CANDIDATE_REPORTS_MAX_POLLS * refreshAfterSeconds * 1000;
}

/** A building kind's phase (see the module note); null when not building. */
export type BuildingPhase = "fast" | "slow" | "stalled";

function phaseAt(ageMs: number, refreshAfterSeconds: number): BuildingPhase {
  if (ageMs >= REFRESH_IN_FLIGHT_MS) return "stalled";
  if (ageMs >= episodeBudgetMs(refreshAfterSeconds)) return "slow";
  return "fast";
}

/** This kind's phase at `now`; a building kind with no recorded start is FAST. */
export function buildingPhase(
  searchId: string,
  candidacyId: string,
  data: CandidateReportsResponse | undefined,
  kind: CandidateReportKind,
  now: number
): BuildingPhase | null {
  const report = data?.reports.find((r) => r.kind === kind);
  if (report?.status !== "building" || !data) return null;
  const started = episodes.get(episodeKey(searchId, candidacyId, kind))?.start;
  return phaseAt(started === undefined ? 0 : now - started, data.refresh_after_seconds);
}

/** Is this kind building past 600 s, where only a Refresh helps? */
export function isStalled(
  searchId: string,
  candidacyId: string,
  data: CandidateReportsResponse | undefined,
  kind: CandidateReportKind,
  now: number
): boolean {
  return buildingPhase(searchId, candidacyId, data, kind, now) === "stalled";
}

/**
 * Milliseconds until the next poll, or `false` to stop. Pure, exported for its
 * test: `youngestEpisodeMs` is the age of the youngest building episode (null
 * when none has a start yet, which is FAST).
 *
 * The API answers `refresh_after_seconds > 0` while ANY kind builds; every
 * observer shows all four kinds (released 2026-10-26, epic
 * 2026-10-22-candidate-reports-four-kinds), so any build keeps it polling.
 */
export function nextCandidateReportsPollInterval(args: {
  data: CandidateReportsResponse | undefined;
  error: unknown;
  youngestEpisodeMs: number | null;
}): number | false {
  if (args.error instanceof CandidateReportNotFoundError) return false;
  const seconds = args.data?.refresh_after_seconds ?? 0;
  if (seconds <= 0) return false;
  const phase = phaseAt(args.youngestEpisodeMs ?? 0, seconds);
  if (phase === "stalled") return false;
  return phase === "slow" ? SLOW_POLL_FACTOR * seconds * 1000 : seconds * 1000;
}

function youngestEpisodeMs(
  searchId: string,
  candidacyId: string,
  data: CandidateReportsResponse | undefined,
  now: number
): number | null {
  const ages = (data?.reports ?? [])
    .filter((r) => r.status === "building")
    .map((r) => episodes.get(episodeKey(searchId, candidacyId, r.kind))?.start)
    .map((started) => (started === undefined ? 0 : now - started));
  return ages.length === 0 ? null : Math.min(...ages);
}

/** One kind's state out of an index answer. */
export function reportStateFor(
  data: CandidateReportsResponse | undefined,
  kind: CandidateReportKind
): CandidateReportState | undefined {
  return data?.reports.find((report) => report.kind === kind);
}

/**
 * Write a `building` answer for `kind` into the shared entry — a Refresh's
 * 202, or a download's 409 (#874). An index read already in flight (a mount
 * refetch) would land after it and overwrite `building` with the old answer,
 * ending the episode just started: it is cancelled first. The episode starts
 * BEFORE the answer lands in the cache: writing it re-evaluates the
 * intervals, which must already see the new start.
 */
export async function seedBuildingAnswer(
  queryClient: QueryClient,
  searchId: string,
  candidacyId: string,
  kind: CandidateReportKind,
  data: CandidateReportsResponse
): Promise<void> {
  const key = candidateReportsQueryKey(searchId, candidacyId);
  await queryClient.cancelQueries({ queryKey: key });
  const version = data.reports.find((r) => r.kind === kind)?.version_count ?? 0;
  startBuildingEpisode(searchId, candidacyId, kind, version, visibleNow());
  recordAndWriteAnswer(queryClient, searchId, candidacyId, data);
}

/**
 * Write an index answer that is NOT a build this click started — a 409 whose
 * body does not show the kind `building` — into the shared entry, so a build
 * that landed or a report deleted in the race shows at once (/pr-review #1114
 * iter 2, m1). An index read in flight is cancelled first, as for a seed.
 */
export async function writeIndexAnswer(
  queryClient: QueryClient,
  searchId: string,
  candidacyId: string,
  data: CandidateReportsResponse
): Promise<void> {
  await queryClient.cancelQueries({ queryKey: candidateReportsQueryKey(searchId, candidacyId) });
  recordAndWriteAnswer(queryClient, searchId, candidacyId, data);
}

function recordAndWriteAnswer(
  queryClient: QueryClient,
  searchId: string,
  candidacyId: string,
  data: CandidateReportsResponse
): void {
  const key = candidateReportsQueryKey(searchId, candidacyId);
  recordBuildingEpisodes(
    searchId,
    candidacyId,
    data,
    visibleNow(),
    queryClient.getQueryData<CandidateReportsResponse>(key)
  );
  queryClient.setQueryData(key, data);
}

/** How a click's wait for its report's rebuild ended. */
export type RebuildOutcome = "present" | "failed" | "stalled" | "gone" | "cancelled";

/**
 * Did the build this wait was for give up? A rebuild that gives up puts the
 * row back to `present` with its OLD files and stamps `last_error_at`. It
 * gave up when that stamp is newer than the one the wait started with (a
 * failure the wait already knew about is not this rebuild's) and newer than
 * the files' `built_at` (the files predate the failed build). Any successful
 * build, unchanged ones included, clears the stamp.
 */
export function rebuildGaveUp(
  state: CandidateReportState | undefined,
  errorAtWaitStart: string | null | undefined
): boolean {
  const failedAt = Date.parse(state?.last_error_at ?? "");
  if (Number.isNaN(failedAt)) return false;
  const before = Date.parse(errorAtWaitStart ?? "");
  if (!Number.isNaN(before) && failedAt <= before) return false;
  const builtAt = Date.parse(state?.built_at ?? "");
  return Number.isNaN(builtAt) || failedAt > builtAt;
}

/** The `built_at` the shared entry holds for a kind: the stored copy's. */
function cachedBuiltAt(
  queryClient: QueryClient,
  searchId: string,
  candidacyId: string,
  kind: CandidateReportKind
): string | null {
  const key = candidateReportsQueryKey(searchId, candidacyId);
  return (
    reportStateFor(queryClient.getQueryData<CandidateReportsResponse>(key), kind)?.built_at ?? null
  );
}

/** Does a 409's body show `kind` being rebuilt? Anything else (a race the
 *  worker had already finished) is not worth a wait. */
function showsBuilding(body: CandidateReportsResponse, kind: CandidateReportKind): boolean {
  return reportStateFor(body, kind)?.status === "building";
}

/** Does an index answer show `kind` gone (`deleted`, or `none`)? */
function showsGone(body: CandidateReportsResponse, kind: CandidateReportKind): boolean {
  const status = reportStateFor(body, kind)?.status;
  return status === "deleted" || status === "none";
}

/** How often the wait re-reads the entry for the stall budget (no answer
 *  arrives once polling has stopped, so the clock is checked on its own). */
const WAIT_CHECK_MS = 1_000;

/**
 * Resolve when `kind` leaves `building` in the shared entry: `present` (the
 * rebuild landed — with new files, or unchanged: an unchanged build keeps its
 * `built_at`, so the click does not wait for a newer one), `failed` (it is
 * `present` again because the rebuild gave up — see `rebuildGaveUp`), `gone`
 * (`deleted` or `none`: the source left Thrive), `stalled` (past the
 * episode's 600 s, the same budget the panel stops polling at), or
 * `cancelled` (`signal`).
 */
export function waitForReportRebuild(
  queryClient: QueryClient,
  searchId: string,
  candidacyId: string,
  kind: CandidateReportKind,
  signal?: AbortSignal
): Promise<RebuildOutcome> {
  const key = candidateReportsQueryKey(searchId, candidacyId);
  const hash = hashKey(key);
  const startedAt = visibleNow();
  const errorAtStart =
    reportStateFor(queryClient.getQueryData<CandidateReportsResponse>(key), kind)?.last_error_at ??
    null;
  const outcomeNow = (): RebuildOutcome | null => {
    const data = queryClient.getQueryData<CandidateReportsResponse>(key);
    const state = reportStateFor(data, kind);
    const status = state?.status;
    const now = visibleNow();
    if (status === "present") return rebuildGaveUp(state, errorAtStart) ? "failed" : "present";
    if (status === "deleted" || status === "none") return "gone";
    if (
      isStalled(searchId, candidacyId, data, kind, now) ||
      now - startedAt >= REFRESH_IN_FLIGHT_MS
    ) {
      return "stalled";
    }
    return null;
  };
  return new Promise((resolve) => {
    const first = signal?.aborted ? "cancelled" : outcomeNow();
    if (first !== null) {
      resolve(first);
      return;
    }
    // Declared before anything can call `settle`: the cache notifies and the
    // interval fires asynchronously, after both exist.
    const check = () => {
      const outcome = outcomeNow();
      if (outcome !== null) settle(outcome);
    };
    const onAbort = () => settle("cancelled");
    const unsubscribe = queryClient.getQueryCache().subscribe((event) => {
      if (event.type === "updated" && event.query.queryHash === hash) check();
    });
    const timer = setInterval(check, WAIT_CHECK_MS);
    signal?.addEventListener("abort", onAbort);
    function settle(outcome: RebuildOutcome): void {
      unsubscribe();
      clearInterval(timer);
      signal?.removeEventListener("abort", onAbort);
      resolve(outcome);
    }
  });
}

/** Abort every wait a component started when it unmounts. */
function useWaitAborts(): () => AbortSignal {
  const controllers = useRef(new Set<AbortController>());
  useEffect(() => {
    const current = controllers.current;
    return () => {
      for (const controller of current) controller.abort();
      current.clear();
    };
  }, []);
  return () => {
    const controller = new AbortController();
    controllers.current.add(controller);
    return controller.signal;
  };
}

export interface UseCandidateReportsArgs {
  client: CandidateReportsClient;
  searchId: string;
  candidacyId: string;
  /** False → nothing is asked (a row the projection flags with no report). */
  enabled?: boolean;
}

export interface UseCandidateReportsResult {
  query: UseQueryResult<CandidateReportsResponse>;
  /** This kind's building phase now (fast / slow / stalled), null when not
   *  building — see the module note. */
  phase: (kind: CandidateReportKind) => BuildingPhase | null;
  /** This kind is building past 600 s: polling stopped, only a Refresh helps. */
  stalled: (kind: CandidateReportKind) => boolean;
}

/** Query clients whose cache already prunes episodes on removal. */
const pruning = new WeakSet<QueryClient>();

/** Drop a candidacy's episodes when its index query leaves the cache (GC): a
 *  later mount starts from a clean slate instead of an old start. */
function pruneEpisodesOnRemoval(queryClient: QueryClient): void {
  if (pruning.has(queryClient)) return;
  pruning.add(queryClient);
  const unsubscribe = queryClient.getQueryCache().subscribe((event) => {
    if (event.type !== "removed") return;
    const [scope, what, searchId, candidacyId] = event.query.queryKey;
    if (scope !== "searches" || what !== "candidate-reports") return;
    forgetBuildingEpisodes(String(searchId), String(candidacyId));
  });
  cacheSubscriptions.push(unsubscribe);
}

export function useCandidateReports({
  client,
  searchId,
  candidacyId,
  enabled = true,
}: UseCandidateReportsArgs): UseCandidateReportsResult {
  const queryClient = useQueryClient();
  useEffect(() => pruneEpisodesOnRemoval(queryClient), [queryClient]);
  const queryKey = candidateReportsQueryKey(searchId, candidacyId);

  const query = useQuery<CandidateReportsResponse>({
    queryKey,
    queryFn: async ({ signal }) => {
      const data = await client.getReports(searchId, candidacyId);
      // A read cancelled under it (Refresh's `cancelQueries`) is discarded by
      // TanStack; its answer must not touch the episodes either — an old
      // `present` would end the episode the Refresh just started.
      if (signal.aborted) return data;
      recordBuildingEpisodes(
        searchId,
        candidacyId,
        data,
        visibleNow(),
        queryClient.getQueryData<CandidateReportsResponse>(queryKey)
      );
      return data;
    },
    enabled: enabled && Boolean(searchId) && Boolean(candidacyId),
    staleTime: REPORTS_STALE_MS,
    refetchInterval: (q) =>
      nextCandidateReportsPollInterval({
        data: q.state.data,
        error: q.state.error,
        youngestEpisodeMs: youngestEpisodeMs(searchId, candidacyId, q.state.data, visibleNow()),
      }),
    retry: (failureCount, error) =>
      !(error instanceof CandidateReportNotFoundError) && failureCount < 2,
  });

  // Read so the hook re-renders on EVERY answer: an unchanged index is
  // structurally shared, so `data` alone would not wake it on the poll that
  // finds the budget spent.
  void query.dataUpdatedAt;
  void query.errorUpdatedAt;

  return {
    query,
    phase: (kind) => buildingPhase(searchId, candidacyId, query.data, kind, visibleNow()),
    stalled: (kind) => isStalled(searchId, candidacyId, query.data, kind, visibleNow()),
  };
}

/**
 * Hand the browser a signed URL to save.
 *
 * The URL is cross-origin, so browsers IGNORE the anchor's `download`
 * attribute and this click is a plain navigation. What makes it a download —
 * and what keeps the page from navigating away — is the
 * `Content-Disposition: attachment; filename=…` the API presigns into the URL
 * (`disposition=attachment`, CONTRACT-03-04 §5). Never call this with an
 * inline URL.
 */
export function openSignedDownload(download: CandidateReportDownload): void {
  const anchor = document.createElement("a");
  anchor.href = download.url;
  anchor.download = download.filename;
  anchor.rel = "noopener";
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
}

export interface ReportDownloadState {
  /** The kind and format whose signed URL is being asked for, if any;
   *  `waiting` while the click waits for its report's rebuild (#874). */
  busy: { kind: CandidateReportKind; format: CandidateReportFormat; waiting: boolean } | null;
  download: (kind: CandidateReportKind, format: CandidateReportFormat) => Promise<void>;
}

/**
 * The click path: ask for a fresh signed URL, open it, toast on failure. A
 * 409 (#874) waits for the rebuild — see the module note — and then opens the
 * new file once.
 */
export function useReportDownload(
  client: CandidateReportsClient,
  searchId: string,
  candidacyId: string
): ReportDownloadState {
  const queryClient = useQueryClient();
  const nextSignal = useWaitAborts();
  const [busy, setBusy] = useState<ReportDownloadState["busy"]>(null);

  const downloadStored = async (kind: CandidateReportKind, format: CandidateReportFormat) => {
    try {
      openSignedDownload(
        await client.getDownload(searchId, candidacyId, kind, format, "attachment", {
          liveCheck: false,
        })
      );
    } catch {
      toast.error(DOWNLOAD_FAILED);
    }
  };

  return {
    busy,
    download: async (kind, format) => {
      setBusy({ kind, format, waiting: false });
      try {
        let signed: CandidateReportDownload;
        // The stored copy is served because a rebuild gave up: just now
        // (`failed`), or recently enough that the API did not try again.
        let gaveUp = false;
        try {
          signed = await client.getDownload(searchId, candidacyId, kind, format, "attachment", {
            liveCheck: true,
          });
          gaveUp = Boolean(signed.rebuild_failed_at);
        } catch (error) {
          if (!(error instanceof CandidateReportRebuildingError)) throw error;
          if (!showsBuilding(error.body, kind)) {
            await writeIndexAnswer(queryClient, searchId, candidacyId, error.body);
            if (showsGone(error.body, kind)) toast.error(REPORT_GONE);
            else await downloadStored(kind, format);
            return;
          }
          setBusy({ kind, format, waiting: true });
          await seedBuildingAnswer(queryClient, searchId, candidacyId, kind, error.body);
          const outcome = await waitForReportRebuild(
            queryClient,
            searchId,
            candidacyId,
            kind,
            nextSignal()
          );
          if (outcome === "cancelled") return;
          if (outcome === "gone") {
            toast.error(REPORT_GONE);
            return;
          }
          if (outcome === "stalled") {
            toast.error(STILL_UPDATING, {
              action: {
                label: "Download stored copy",
                onClick: () => void downloadStored(kind, format),
              },
            });
            return;
          }
          gaveUp = outcome === "failed";
          signed = await client.getDownload(searchId, candidacyId, kind, format, "attachment", {
            liveCheck: false,
          });
        }
        if (gaveUp) {
          const built = cachedBuiltAt(queryClient, searchId, candidacyId, kind);
          toast.error(rebuildFailedMessage(built, "Downloading"));
        }
        openSignedDownload(signed);
      } catch {
        toast.error(DOWNLOAD_FAILED);
      } finally {
        setBusy(null);
      }
    },
  };
}

/**
 * "Open PDF": a fresh inline URL per click, in a new tab. The tab is opened
 * BEFORE the request, synchronously inside the click, so a popup blocker sees
 * a user gesture; it is pointed at the URL when it arrives and closed if the
 * request fails. A BLOCKED popup (`window.open` → null) is not retried after
 * the await — no gesture is left by then; the toast says so and carries an
 * "Open PDF" action, whose click IS a gesture.
 *
 * The tab says what it is waiting for in its own document, so a blank page
 * does not read as a broken one: "Checking…" while the live check reads
 * Thrive, then — on a 409 (#874), while the report rebuilds (`waiting`) —
 * "Updating…"; it is then pointed at the new file. A rebuild that gave up
 * (now, or recently: `rebuild_failed_at`) points it at the stored file with
 * an error toast. A stalled or vanished report closes it; the stall's toast
 * offers the stored PDF, in a tab its own click opens. A blocked popup gets
 * ONE toast, the popup one: its action asks again, and a rebuild that gave up
 * is then reported through `rebuild_failed_at`.
 */
export function useOpenPdf(client: CandidateReportsClient, searchId: string, candidacyId: string) {
  const queryClient = useQueryClient();
  const nextSignal = useWaitAborts();
  const [busy, setBusy] = useState(false);
  const [waiting, setWaiting] = useState(false);
  const open = async (kind: CandidateReportKind, liveCheck = true): Promise<void> => {
    const tab = window.open("about:blank", "_blank");
    if (tab) {
      tab.opener = null;
      if (liveCheck) writeTabMessage(tab, CHECKING_TAB_MESSAGE);
    }
    setBusy(true);
    try {
      let signed: CandidateReportDownload;
      let gaveUp = false;
      try {
        signed = await client.getDownload(searchId, candidacyId, kind, "pdf", "inline", {
          liveCheck,
        });
        gaveUp = Boolean(signed.rebuild_failed_at);
      } catch (error) {
        if (!(error instanceof CandidateReportRebuildingError)) throw error;
        let outcome: RebuildOutcome = "present";
        if (!showsBuilding(error.body, kind)) {
          await writeIndexAnswer(queryClient, searchId, candidacyId, error.body);
          if (showsGone(error.body, kind)) outcome = "gone";
        } else {
          setWaiting(true);
          if (tab) writeTabMessage(tab, UPDATING_TAB_MESSAGE);
          await seedBuildingAnswer(queryClient, searchId, candidacyId, kind, error.body);
          outcome = await waitForReportRebuild(
            queryClient,
            searchId,
            candidacyId,
            kind,
            nextSignal()
          );
        }
        if (outcome === "failed") gaveUp = true;
        else if (outcome !== "present") {
          tab?.close();
          if (outcome === "gone") toast.error(REPORT_GONE);
          if (outcome === "stalled") {
            toast.error(STILL_UPDATING, {
              action: { label: "Open stored PDF", onClick: () => void open(kind, false) },
            });
          }
          return;
        }
        signed = await client.getDownload(searchId, candidacyId, kind, "pdf", "inline", {
          liveCheck: false,
        });
      }
      if (tab) {
        if (gaveUp) {
          const built = cachedBuiltAt(queryClient, searchId, candidacyId, kind);
          toast.error(rebuildFailedMessage(built, "Opening"));
        }
        tab.location.href = signed.url;
      } else {
        // The action runs the whole click again — a FRESH presign: the toast
        // can outlive this URL (Sonner pauses while hovered or hidden).
        toast.error(POPUP_BLOCKED, {
          action: { label: "Open PDF", onClick: () => void open(kind, liveCheck) },
        });
      }
    } catch {
      tab?.close();
      toast.error(OPEN_PDF_FAILED);
    } finally {
      setBusy(false);
      setWaiting(false);
    }
  };
  return { busy, waiting, open };
}

/**
 * Put a line of plain text in a tab this page opened on `about:blank` (same
 * origin, so its document is ours to write). Text only, no markup or styles.
 * A tab the user closed, or a browser that refuses, is left as it is.
 */
export function writeTabMessage(tab: Window, message: string): void {
  try {
    tab.document.title = message;
    if (tab.document.body) tab.document.body.textContent = message;
  } catch {
    // Nothing to say it in; the button still shows "Updating from Thrive…".
  }
}

/** Refresh from Thrive: POST, start a new episode, write the 202 into the
 *  shared entry — which re-evaluates every observer's interval. */
export function useRefreshCandidateReport(
  client: CandidateReportsClient,
  searchId: string,
  candidacyId: string
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (kind: CandidateReportKind) => client.refresh(searchId, candidacyId, kind),
    onSuccess: (data, kind) => seedBuildingAnswer(queryClient, searchId, candidacyId, kind, data),
    onError: () => {
      toast.error(REFRESH_FAILED);
    },
  });
}

/**
 * The stored PDF's inline URL for the panel preview, whenever `formats` has
 * the PDF (`present`, or `building` on top of a previous build).
 * Not kept once unobserved (`gcTime: 0`), so switching kinds and back, a new
 * version, or another candidate asks again. Nothing re-asks it while the frame
 * shows it: the frame already holds the document, and "Open PDF" presigns its
 * own. The panel still pins the URL its frame loaded, so a refetch from any
 * other trigger (a window-focus refetch in a client that enables it) cannot
 * reload the document under the reader.
 *
 * No live check (#874): viewing is not a click. Paging through the panel
 * must not spend a Thrive request per candidate, nor queue a rebuild of every
 * report it shows; a Download or "Open PDF" checks. A rebuild that lands
 * changes `built_at`, which is in this query's key, so the frame reloads.
 */
export function useReportPreviewUrl(
  client: CandidateReportsClient,
  searchId: string,
  candidacyId: string,
  /** The panel's selected kind; null when the candidate offers none. */
  kind: CandidateReportKind | null,
  /** That kind's index entry. */
  state: CandidateReportState | undefined
): UseQueryResult<CandidateReportDownload> {
  // Whatever the status: while `building`, `formats` names the previous
  // build's files, which stay the preview until the new one lands.
  const ready = kind !== null && Boolean(state?.formats.includes("pdf"));
  return useQuery<CandidateReportDownload>({
    queryKey: [
      "searches",
      "candidate-report-preview",
      searchId,
      candidacyId,
      kind,
      state?.built_at,
      state?.version_count,
    ],
    queryFn: () => {
      // `enabled` holds this off while there is no kind; the guard is for tsc.
      if (kind === null) throw new Error("No report kind selected.");
      return client.getDownload(searchId, candidacyId, kind, "pdf", "inline");
    },
    enabled: ready,
    staleTime: 0,
    gcTime: 0,
    retry: false,
  });
}
